import { Info as InformationCircleIcon } from 'lucide-react';
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';

const MacroList = function MacroList({
  ref,
  items,
  command,
}: {
  items: {
    id: string;
    shortcut: string;
    phrase: string;
  }[];
  command: (args: { id: string; label: string }) => void;
} & {
  ref: React.RefObject<unknown>;
}) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  const selectItem = (index: number) => {
    const item = items[index];

    if (item) {
      command({
        id: item.id,
        label: item.phrase,
      });
    }
  };

  const upHandler = () => {
    setSelectedIndex((selectedIndex + items.length - 1) % items.length);
  };

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % items.length);
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
  };

  useEffect(() => setSelectedIndex(0), [items]);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && listRef.current) {
      const listItems = listRef.current.querySelectorAll('li');
      const selectedItem = listItems[selectedIndex];
      if (selectedItem) {
        selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp') {
        upHandler();

        return true;
      }

      if (event.key === 'ArrowDown') {
        downHandler();

        return true;
      }

      if (event.key === 'Enter') {
        enterHandler();

        return true;
      }

      if (event.key === 'Tab') {
        enterHandler();

        return true;
      }

      return false;
    },
  }));

  return (
    <div className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg flex flex-col">
      <p className="italic p-1 text-xs text-gray-500 dark:text-gray-300 flex items-center bg-gray-300 dark:bg-gray-700 px-2 border-b border-gray-300 dark:border-gray-600 rounded-t-md">
        <span>
          <InformationCircleIcon className="h-4 w-4 mr-2" />
        </span>
        <span>Click on shortcut or press tab to use macro</span>
      </p>
      <div className="p-2 max-h-48 overflow-y-auto">
        {items.length ? (
          <ul ref={listRef} className="list-none">
            {items.map((item, index) => (
              <li
                key={index}
                className={`cursor-pointer py-1 px-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 ${
                  index === selectedIndex ? 'bg-gray-100 dark:bg-gray-700 font-semibold' : ''
                } rounded-md`}
                onClick={() => selectItem(index)}
              >
                {item.shortcut}
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-gray-500 dark:text-gray-400 p-2">No matches</div>
        )}
      </div>
    </div>
  );
};

export default MacroList;
